import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'
import type { InquiryField } from '@/types'

import { useEffect, useRef, useState } from 'react'
import { Check, LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ButtonArrow, buttonStyles } from '@/components/generals/button-link'
import { budgets, projectTypes } from '@/constants/content'

type FormValues = Record<InquiryField, string> & {
    website: string
}

type Status = 'idle' | 'submitting' | 'success' | 'error'

type FieldErrors = Partial<Record<InquiryField, string>>

type ContactResponse = {
    ok?: boolean
    message?: string
    errors?: FieldErrors
}

const initialValues: FormValues = {
    name: '',
    email: '',
    company: '',
    projectType: '',
    budget: '',
    message: '',
    website: ''
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

const validate = (values: FormValues): FieldErrors => {
    const errors: FieldErrors = {}

    if (values.name.trim().length < 2) errors.name = "Please tell me your name."
    if (!EMAIL_PATTERN.test(values.email.trim())) errors.email = "Please enter a valid email address."
    if (values.message.trim().length < 10) errors.message = "A sentence or two about your project helps me reply properly."

    return errors
}

const inputClassName = 'w-full rounded-xl border border-input bg-card px-4 text-base text-foreground transition-colors placeholder:text-muted-foreground/70 hover:border-foreground/40 focus:border-foreground aria-[invalid=true]:border-destructive'

const pillClassName = 'cursor-pointer select-none rounded-full border px-4 py-2 text-sm font-medium transition-colors has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-brand'

interface FieldProps {
    id: string
    label: string
    error?: string
    optional?: boolean
    className?: string
    children: ReactNode
}

const Field: FC<FieldProps> = ({ id, label, error, optional = false, className, children }): ReactNode => {
    return (
        <div className={className}>
            <label htmlFor={id} className='mb-2 flex items-baseline justify-between gap-4 text-sm font-medium'>
                {label}

                {optional && (
                    <span className='font-normal text-muted-foreground'>
                        Optional
                    </span>
                )}
            </label>

            {children}

            {error && (
                <p id={`${id}-error`} className='mt-2 text-sm text-destructive'>
                    {error}
                </p>
            )}
        </div>
    )
}

interface PillGroupProps {
    legend: string
    name: 'projectType' | 'budget'
    options: string[]
    value: string
    onChange: (event: ChangeEvent<HTMLInputElement>) => void
}

const PillGroup: FC<PillGroupProps> = ({ legend, name, options, value, onChange }): ReactNode => {
    return (
        <fieldset>
            <legend className='font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground'>
                {legend}
            </legend>

            <div className='mt-4 flex flex-wrap gap-2'>
                {options.map(option => (
                    <label
                        key={option}
                        className={cn(
                            pillClassName,
                            value === option
                                ? 'border-foreground bg-foreground text-background'
                                : 'border-foreground/20 hover:border-foreground'
                        )}
                    >
                        <input
                            type='radio'
                            name={name}
                            value={option}
                            checked={value === option}
                            onChange={onChange}
                            className='sr-only'
                        />

                        {option}
                    </label>
                ))}
            </div>
        </fieldset>
    )
}

export const ContactForm: FC = (): ReactNode => {
    const [values, setValues] = useState<FormValues>(initialValues)
    const [errors, setErrors] = useState<FieldErrors>({})
    const [status, setStatus] = useState<Status>('idle')
    const [serverError, setServerError] = useState<string | null>(null)
    const [firstName, setFirstName] = useState('')
    const successHeadingRef = useRef<HTMLHeadingElement>(null)

    useEffect(() => {
        if (status === 'success') successHeadingRef.current?.focus()
    }, [status])

    const update = (field: keyof FormValues) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        const { value } = event.target

        setValues(current => ({ ...current, [field]: value }))

        if (field !== 'website' && errors[field]) {
            setErrors(current => ({ ...current, [field]: undefined }))
        }
    }

    const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        if (status === 'submitting') return

        const found = validate(values)
        setErrors(found)

        const firstInvalid = (['name', 'email', 'message'] as const).find(field => found[field])

        if (firstInvalid) {
            document.getElementById(`inquiry-${firstInvalid}`)?.focus()
            return
        }

        setStatus('submitting')
        setServerError(null)

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values)
            })

            const data = await response.json().catch(() => null) as ContactResponse | null

            if (!response.ok || !data?.ok) {
                if (data?.errors) setErrors(data.errors)
                setServerError(data?.message ?? "Something went wrong on my side. Please try again in a moment.")
                setStatus('error')
                return
            }

            setFirstName(values.name.trim().split(/\s+/)[0] ?? '')
            setValues(initialValues)
            setErrors({})
            setStatus('success')
        }
        catch {
            setServerError("Your message could not be sent. Please check your connection and try again.")
            setStatus('error')
        }
    }

    if (status === 'success') {
        return (
            <div data-surface='paper' className='flex min-h-[34rem] flex-col items-start justify-center rounded-3xl bg-background p-8 text-foreground shadow-[0_40px_100px_-40px_rgba(10,10,40,0.6)] md:p-12'>
                <span className='grid size-16 place-items-center rounded-full bg-brand text-brand-foreground'>
                    <Check className='size-8' />
                </span>

                <h3
                    ref={successHeadingRef}
                    tabIndex={-1}
                    className='mt-8 font-heading text-4xl font-semibold leading-[1.02] tracking-[-0.04em] focus:outline-none md:text-5xl'
                >
                    Thanks{firstName ? `, ${firstName}` : ''}! Your message is in.
                </h3>

                <p className='mt-4 max-w-md text-lg leading-relaxed text-muted-foreground'>
                    I read every inquiry personally and will get back to you within two business days.
                </p>

                <button type='button' onClick={() => setStatus('idle')} className={cn(buttonStyles('outline', 'md'), 'mt-8')}>
                    Send another message
                </button>
            </div>
        )
    }

    return (
        <form
            onSubmit={submit}
            noValidate
            data-surface='paper'
            aria-describedby={serverError ? 'inquiry-server-error' : undefined}
            className='rounded-3xl bg-background p-6 text-foreground shadow-[0_40px_100px_-40px_rgba(10,10,40,0.6)] sm:p-8 md:p-10'
        >
            <div className='space-y-8'>
                <PillGroup
                    legend="What do you need?"
                    name='projectType'
                    options={projectTypes}
                    value={values.projectType}
                    onChange={update('projectType')}
                />

                <PillGroup
                    legend="Rough budget"
                    name='budget'
                    options={budgets}
                    value={values.budget}
                    onChange={update('budget')}
                />
            </div>

            <div className='mt-10 grid gap-6 sm:grid-cols-2'>
                <Field id='inquiry-name' label="Your name" error={errors.name}>
                    <input
                        id='inquiry-name'
                        name='name'
                        type='text'
                        autoComplete='name'
                        value={values.name}
                        onChange={update('name')}
                        aria-invalid={Boolean(errors.name)}
                        aria-describedby={errors.name ? 'inquiry-name-error' : undefined}
                        placeholder="Jane Doe"
                        className={cn(inputClassName, 'h-13')}
                    />
                </Field>

                <Field id='inquiry-email' label="Email" error={errors.email}>
                    <input
                        id='inquiry-email'
                        name='email'
                        type='email'
                        inputMode='email'
                        autoComplete='email'
                        value={values.email}
                        onChange={update('email')}
                        aria-invalid={Boolean(errors.email)}
                        aria-describedby={errors.email ? 'inquiry-email-error' : undefined}
                        placeholder="jane@company.com"
                        className={cn(inputClassName, 'h-13')}
                    />
                </Field>

                <Field id='inquiry-company' label="Company or website" optional className='sm:col-span-2' error={errors.company}>
                    <input
                        id='inquiry-company'
                        name='company'
                        type='text'
                        autoComplete='organization'
                        value={values.company}
                        onChange={update('company')}
                        aria-invalid={Boolean(errors.company)}
                        aria-describedby={errors.company ? 'inquiry-company-error' : undefined}
                        className={cn(inputClassName, 'h-13')}
                    />
                </Field>

                <Field id='inquiry-message' label="Tell me about your project" className='sm:col-span-2' error={errors.message}>
                    <textarea
                        id='inquiry-message'
                        name='message'
                        rows={5}
                        value={values.message}
                        onChange={update('message')}
                        aria-invalid={Boolean(errors.message)}
                        aria-describedby={errors.message ? 'inquiry-message-error' : undefined}
                        placeholder="What are you building, who is it for, and when would you love to launch?"
                        className={cn(inputClassName, 'min-h-36 resize-y py-3.5 leading-relaxed')}
                    />
                </Field>
            </div>

            <div aria-hidden='true' className='sr-only'>
                <label htmlFor='inquiry-website'>Leave this field empty</label>
                <input
                    id='inquiry-website'
                    name='website'
                    type='text'
                    tabIndex={-1}
                    autoComplete='off'
                    value={values.website}
                    onChange={update('website')}
                />
            </div>

            {serverError && (
                <p id='inquiry-server-error' role='alert' className='mt-6 rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive'>
                    {serverError}
                </p>
            )}

            <div className='mt-8 flex flex-col gap-5 border-t border-foreground/10 pt-8 sm:flex-row sm:items-center sm:justify-between'>
                <p className='max-w-xs text-sm leading-relaxed text-muted-foreground'>
                    Your details are only used to reply to your inquiry. No newsletters, ever.
                </p>

                <button
                    type='submit'
                    disabled={status === 'submitting'}
                    className={buttonStyles('ink', 'lg')}
                >
                    {status === 'submitting' ? (
                        <>
                            <LoaderCircle className='size-4 animate-spin' />
                            Sending
                        </>
                    ) : (
                        <>
                            Send inquiry
                            <ButtonArrow />
                        </>
                    )}
                </button>
            </div>
        </form>
    )
}
